import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, GraduationCap, Search, Shirt, Users } from 'lucide-react';
import ModernGallery from '../components/gallery/ModernGallery';
import CountdownTimer from '../components/countdown/CountdownTimer';
import HomepageBlog from '../components/blog/HomepageBlog';
import AlumniCarousel from '../components/alumni/AlumniCarousel';
import AlumniShowcase from '../components/AlumniShowcase/AlumniShowcase';
import api from '../services/api';
import { assetUrl } from '../utils/assets';

const FALLBACK_GALLERY = Array.from({ length: 18 }, (_, index) => ({
  id: index + 1,
  src: `/images/${index + 1}.jpg`,
  title: '2026 1st Graduation Ceremony',
}));

/** Uploaded files live on the API; assets bundled in public/ stay relative. */
const resolveGallerySrc = (src) =>
  src && src.startsWith('/uploads') ? assetUrl(src) : src;

const HomePage = () => {
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const [stats, setStats] = useState({
    totalGraduates: null,
    totalDepartments: null,
    totalCourses: null,
  });
  const [countdown, setCountdown] = useState(null);
  const [alumniCount, setAlumniCount] = useState(0);
  const [galleryImages, setGalleryImages] = useState(FALLBACK_GALLERY);
  // Upcoming/active ceremony year, read from the database so the homepage badge
  // updates from the admin panel without a rebuild.
  const [activeGradYear, setActiveGradYear] = useState(null);

  useEffect(() => {
    fetchStatistics();
    fetchCountdown();
    fetchAlumniCount();
    fetchGallery();
    fetchActiveGraduationYear();
  }, []);

  const fetchActiveGraduationYear = async () => {
    try {
      const response = await api.get('/graduation-days/active');
      const year = response.data?.year ?? response.data?.graduation_year;
      if (year) setActiveGradYear(Number(year));
    } catch (error) {
      console.error('Error fetching active graduation year:', error);
    }
  };

  const fetchGallery = async () => {
    try {
      const response = await api.get('/gallery');
      if (Array.isArray(response.data) && response.data.length > 0) {
        setGalleryImages(
          response.data.map((item) => ({
            id: item.id,
            src: resolveGallerySrc(item.src),
            title: item.title || 'Graduation ceremony',
          }))
        );
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const [graduatesRes, departmentsRes, coursesRes] = await Promise.all([
        api.get('/graduates'),
        api.get('/departments'),
        api.get('/courses'),
      ]);
      setStats({
        totalGraduates: graduatesRes.data.length,
        totalDepartments: departmentsRes.data.length,
        totalCourses: coursesRes.data.length,
      });
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const fetchCountdown = async () => {
    try {
      const response = await api.get('/countdown');
      if (response.data?.is_active === true) {
        setCountdown(response.data);
      }
    } catch (error) {
      console.error('Error fetching countdown:', error);
    }
  };

  const fetchAlumniCount = async () => {
    try {
      const response = await api.get('/alumni/total-count');
      if (response.data?.success) {
        setAlumniCount(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching alumni count:', error);
    }
  };

  const statItems = [
    { label: 'Graduates', value: stats.totalGraduates, suffix: '+' },
    { label: 'Departments', value: stats.totalDepartments, suffix: '' },
    { label: 'Courses', value: stats.totalCourses, suffix: '+' },
  ];

  return (
    <div>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative isolate flex min-h-[540px] flex-col justify-center overflow-hidden bg-brand-950 py-16 sm:min-h-[600px]"
      >
        <video
          autoPlay
          loop
          muted
          playsInline
          poster="/images/hero-poster.jpg"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
        >
          <source src="/videos/katine.mp4" type="video/mp4" />
        </video>
        <div
          className="absolute inset-0 bg-gradient-to-r from-brand-950/95 via-brand-950/80 to-brand-950/40"
          aria-hidden="true"
        />

        <div className="container-page relative z-10">
          <div className="max-w-3xl">
            <span className="eyebrow animate-fade-up">
              <GraduationCap className="h-3.5 w-3.5" aria-hidden="true" />
              {activeGradYear ?? new Date().getFullYear()} Graduation
            </span>

            <h1 className="mt-5 text-3xl font-extrabold leading-[1.1] text-white animate-fade-up sm:text-4xl md:text-5xl">
              Verify your graduation status
              <span className="block text-gold-400">in seconds.</span>
            </h1>

            <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/80 animate-fade-up sm:text-base">
              Search the official Katine TTI graduation list by admission or telephone
              number, request your gown, and stay connected with the alumni community.
            </p>

            <div className="mt-8 flex flex-wrap gap-3 animate-fade-up">
              <Link to="/search" className="btn-secondary">
                <Search className="h-4 w-4" aria-hidden="true" />
                Search Graduates
              </Link>
              <Link
                to="/gown-request"
                className="btn border-2 border-white/30 bg-white/5 text-white backdrop-blur-sm hover:border-white/60 hover:bg-white/10"
              >
                <Shirt className="h-4 w-4" aria-hidden="true" />
                Request Gown
              </Link>
            </div>
          </div>
        </div>

        {/* Stats bar pinned to the bottom edge */}
        <div className="container-page relative z-10 mt-12">
          <dl className="grid grid-cols-3 divide-x divide-white/15 overflow-hidden rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md">
            {statItems.map(({ label, value, suffix }) => (
              <div key={label} className="px-4 py-4 text-center sm:px-6 sm:py-5">
                <dd className="text-xl font-bold text-gold-400 sm:text-3xl">
                  {value === null ? (
                    <span className="inline-block h-6 w-12 animate-pulse rounded bg-white/20 align-middle" />
                  ) : (
                    <>
                      {value}
                      {suffix}
                    </>
                  )}
                </dd>
                <dt className="mt-1 text-2xs uppercase tracking-widest text-white/70 sm:text-xs">
                  {label}
                </dt>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ── Countdown ────────────────────────────────────────────────────── */}
      {countdown?.is_active === true && countdown.ceremony_date && (
        <CountdownTimer
          targetDate={countdown.ceremony_date}
          title={countdown.title}
          message={countdown.message}
        />
      )}

      {/* ── Latest news ──────────────────────────────────────────────────── */}
      <HomepageBlog />

      {/* ── Alumni committee ─────────────────────────────────────────────── */}
      <section className="section bg-white">
        <div className="container-page">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="eyebrow">
                <Users className="h-3.5 w-3.5" aria-hidden="true" />
                Alumni Network
              </span>
              <h2 className="section-title mt-3">Alumni Committee</h2>
              <p className="section-subtitle">
                Meet the graduates leading the Katine TTI alumni association.
              </p>
              <div className="rule-gold" aria-hidden="true" />
            </div>
            <Link
              to="/alumni"
              className="btn-ghost hidden sm:inline-flex"
            >
              View all alumni
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          <AlumniCarousel autoScroll scrollSpeed={0.8} />

          <div className="mt-8 text-center sm:hidden">
            <button type="button" onClick={() => navigate('/alumni')} className="btn-outline">
              View all alumni
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>

      {/* ── Gallery ──────────────────────────────────────────────────────── */}
      <section className="section bg-ink-50">
        <div className="container-page">
          <div className="mb-8">
            <span className="eyebrow">Memories</span>
            <h2 className="section-title mt-3">Gallery</h2>
            <p className="section-subtitle">
              Relive the special moments from our past ceremonies, visits and events.
            </p>
            <div className="rule-gold" aria-hidden="true" />
          </div>

          <ModernGallery images={galleryImages} />
        </div>
      </section>

      {/* ── Alumni community CTA ─────────────────────────────────────────── */}
      <section className="relative isolate overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/community.png')" }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 bg-gradient-to-r from-brand-950/95 via-brand-950/85 to-brand-900/70"
          aria-hidden="true"
        />

        <div className="container-page relative z-10 py-16 sm:py-20">
          <div className="max-w-2xl">
            <span className="eyebrow bg-white/10 text-gold-300">Alumni Community</span>
            <h2 className="mt-4 text-2xl font-bold text-white sm:text-3xl">
              Join {alumniCount > 0 ? `${alumniCount}+ ` : ''}graduates in our alumni family
            </h2>
            <p className="mt-3 text-sm text-white/75 sm:text-base">
              Register to reconnect with classmates, nominate committee members, vote in
              alumni elections, and get ceremony updates.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/alumni/register" className="btn-secondary">
                <GraduationCap className="h-4 w-4" aria-hidden="true" />
                Join the alumni
              </Link>
              <Link
                to="/alumni"
                className="btn border-2 border-white/30 text-white hover:border-white/60 hover:bg-white/10"
              >
                Meet the committee
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="mt-12">
            <AlumniShowcase />
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
